/**
 * Rounds played together on one device, by taking turns, as they cross the
 * wire (`docs/decisions/0059`).
 *
 * A round is kept as a record of its own, **apart from everything online**:
 * it never reaches a personal best, the solo statistics, the daily or any
 * leaderboard. A guest keeps the last `LOCAL_SESSION_LIMIT` rounds on the
 * device; a signed-in player's device also syncs them with the account
 * (`PUT /api/account/local-sessions`), so the same history shows on every
 * device they sign in on. This file is the one shape both ends read, the one
 * validator both ends trust, and the one merge rule both ends apply, so the
 * client's store and the server's table can never disagree about what a
 * round is or which fifty are kept.
 *
 * Pure: no DOM, no Node, no clock (the caller passes every time).
 */
import { decodeModeConfig } from '@interlock/modes';

/** The most rounds kept, on a device and on an account alike: the newest fifty. */
export const LOCAL_SESSION_LIMIT = 50;

/** How many take turns in one round. */
export const LOCAL_PLAYERS_MIN = 2;
export const LOCAL_PLAYERS_MAX = 4;

/** The longest name a seat may have, in characters (the shell's `PlayerNames` default). */
export const LOCAL_NAME_MAX_LENGTH = 16;

/** A round's id: made on the device that played it, random, and the key the merge unions on. */
export const LOCAL_SESSION_ID_PATTERN = /^[A-Za-z0-9-]{8,40}$/;

/** The largest line count or duration a turn may report: generous, and a fence against nonsense. */
export const LOCAL_TURN_LINES_MAX = 100_000;
export const LOCAL_TURN_TIME_MAX_MS = 24 * 60 * 60 * 1000;

/**
 * How far ahead of the server's clock a round's `playedAtMs` may be: a day,
 * for a device whose clock is off, and no further. A round "played" later
 * than that would sort above every real round and hold its place among the
 * newest fifty until the clock caught up, so the server refuses it
 * (`parseLocalSessions`'s `latestPlayedAtMs`).
 */
export const LOCAL_SESSION_FUTURE_SLACK_MS = 24 * 60 * 60 * 1000;

/**
 * How a turn ended:
 *
 * - `finished`: the run reached its end on its own terms (a Sprint's goal or
 *   clock; for Survival, being buried is the end every run has);
 * - `topped-out`: a Sprint that topped out before its goal or its clock;
 * - `quit`: the player ended the turn from the quit question.
 */
export type LocalTurnOutcome = 'finished' | 'topped-out' | 'quit';

const OUTCOMES: readonly LocalTurnOutcome[] = ['finished', 'topped-out', 'quit'];

/** One seat's line in a finished round, in turn order. */
export interface LocalSessionPlayer {
  readonly name: string;
  /** Competition rank, 1 first; a tie shares one. */
  readonly rank: number;
  readonly outcome: LocalTurnOutcome;
  readonly lines: number;
  readonly timeMs: number;
}

/** One finished round. */
export interface LocalSession {
  readonly id: string;
  readonly playedAtMs: number;
  /** `encodeModeConfig` of the round's mode, goal and hardness: one every player played. */
  readonly modeKey: string;
  readonly players: readonly LocalSessionPlayer[];
}

/** Control and format characters: never part of a name (the shell's `resolvePlayerNames` drops them too). */
const INVISIBLE = /[\p{Cc}\p{Cf}]/u;

function isWhole(value: unknown, max: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= max;
}

function parseName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  if (raw !== raw.trim() || INVISIBLE.test(raw)) return null;
  const length = Array.from(raw).length;
  return length >= 1 && length <= LOCAL_NAME_MAX_LENGTH ? raw : null;
}

function parsePlayer(raw: unknown, seats: number): LocalSessionPlayer | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const entry = raw as Record<string, unknown>;
  const name = parseName(entry['name']);
  const rank = entry['rank'];
  const outcome = entry['outcome'];
  const lines = entry['lines'];
  const timeMs = entry['timeMs'];
  if (name === null) return null;
  if (!isWhole(rank, seats) || rank < 1) return null;
  if (typeof outcome !== 'string' || !(OUTCOMES as readonly string[]).includes(outcome)) {
    return null;
  }
  if (!isWhole(lines, LOCAL_TURN_LINES_MAX) || !isWhole(timeMs, LOCAL_TURN_TIME_MAX_MS)) {
    return null;
  }
  return { name, rank, outcome: outcome as LocalTurnOutcome, lines, timeMs };
}

/**
 * One round, or `null` when anything in it is malformed: the whole round is
 * refused rather than repaired, since a round with a seat missing would rank
 * the rest wrongly. Only a mode the game offers solo is accepted
 * (`decodeModeConfig`'s exact keys).
 */
export function parseLocalSession(raw: unknown): LocalSession | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const entry = raw as Record<string, unknown>;
  const id = entry['id'];
  const playedAtMs = entry['playedAtMs'];
  const modeKey = entry['modeKey'];
  const rawPlayers = entry['players'];
  if (typeof id !== 'string' || !LOCAL_SESSION_ID_PATTERN.test(id)) return null;
  if (!isWhole(playedAtMs, 8.64e15)) return null;
  if (typeof modeKey !== 'string' || decodeModeConfig(modeKey) === null) return null;
  if (!Array.isArray(rawPlayers)) return null;
  if (rawPlayers.length < LOCAL_PLAYERS_MIN || rawPlayers.length > LOCAL_PLAYERS_MAX) return null;
  const players: LocalSessionPlayer[] = [];
  for (const rawPlayer of rawPlayers) {
    const player = parsePlayer(rawPlayer, rawPlayers.length);
    if (player === null) return null;
    players.push(player);
  }
  return { id, playedAtMs, modeKey, players };
}

/**
 * Whatever a client or a stored copy holds, as rounds: each malformed one is
 * dropped and the rest kept (the policy the solo statistics and the bests
 * follow: one unreadable entry from an older build must not stop the rest).
 * At most `limit` are read, newest first after the merge rule below; a list
 * longer than twice the limit is not read past that point at all.
 *
 * `latestPlayedAtMs`, when given, also drops a round played after it: the
 * server passes its own clock plus `LOCAL_SESSION_FUTURE_SLACK_MS`. It is
 * applied before the newest `limit` are chosen, so a round from the future
 * cannot take a real round's place.
 */
export function parseLocalSessions(
  raw: unknown,
  limit: number = LOCAL_SESSION_LIMIT,
  latestPlayedAtMs: number = Number.POSITIVE_INFINITY,
): LocalSession[] {
  if (!Array.isArray(raw)) return [];
  const parsed: LocalSession[] = [];
  for (const entry of raw.slice(0, limit * 2)) {
    const session = parseLocalSession(entry);
    if (session !== null && session.playedAtMs <= latestPlayedAtMs) parsed.push(session);
  }
  return mergeLocalSessions([parsed], limit);
}

/** Newest first; two rounds of the same instant in id order, so every store sorts alike. */
export function compareLocalSessions(a: LocalSession, b: LocalSession): number {
  if (a.playedAtMs !== b.playedAtMs) return b.playedAtMs - a.playedAtMs;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * The merge rule, for the device and the account alike: the union of the
 * lists by id (a round already held is kept as it is, so the earlier list
 * wins a clash: on the server, what is stored), newest first, the newest
 * `limit` kept. A round is never changed after it is played, so a union is
 * exact: two devices that each played rounds end up with both sets, and a
 * round pushed twice is one round.
 */
export function mergeLocalSessions(
  lists: readonly (readonly LocalSession[])[],
  limit: number = LOCAL_SESSION_LIMIT,
): LocalSession[] {
  const byId = new Map<string, LocalSession>();
  for (const list of lists) {
    for (const session of list) {
      if (!byId.has(session.id)) byId.set(session.id, session);
    }
  }
  return [...byId.values()].sort(compareLocalSessions).slice(0, Math.max(0, limit));
}
